import { Scripts } from '@core/data-access/models/script-injector.model';
import { Action } from '@ngrx/store';
import { QuestionnaireDataInput } from '../customization/customization.interfaces';
import { ExhibitionHistoryListModel, ExhibitionModel, SelectOption } from './exhibition.interface';

export const enum ActionTypes {
  SET_SELECTED_EXHIBITION_ID = '[Exhibition] Set selected exhibition id',
  SET_IS_FIRST_LOAD_DONE = '[Exhibition] Set is first load done',
  GET_LIST_OF_ALL_EXHIBITIONS = '[Exhibition] Get list of all exhibitions from API',
  ADD_ALL_EXHIBITIONS_TO_LIST = '[Exhibition] Add all exhibitions to a list',
  GET_EXHIBITION_HISTORY_LIST = '[Exhibition] Get Exhibition History List',
  SET_EXHIBITION_HISTORY_LIST = '[Exhibition] Set Exhibition History List',
  GET_SCRIPTS = '[Exhibition] Get scripts',
  SET_SCRIPTS = '[Exhibition] Set scripts',
  RELOAD_SCRIPTS = '[Exhibition] Reload scripts',
  RESET_SCRIPTS = '[Exhibition] Reset scripts',
  GET_TITLES = '[Exhibition] Get title',
  SET_TITLES = '[Exhibition] Set title',
  GET_PROFESSIONS = '[Exhibition] Get professions',
  SET_PROFESSIONS = '[Exhibition] Set professions',
  GET_DEPARTMENTS = '[Exhibition] Get departments',
  SET_DEPARTMENTS = '[Exhibition] Set departments',
  GET_OCCUPATIONAL_GROUPS = '[Exhibition] Get occupational groups',
  SET_OCCUPATIONAL_GROUPS = '[Exhibition] Set occupational groups',
  GET_ALL_TITLES = '[Exhibition] Get all titles',
  SET_ALL_TITLES = '[Exhibition] Set all titles',
  GET_ALL_PROFESSIONS = '[Exhibition] Get all professions',
  SET_ALL_PROFESSIONS = '[Exhibition] Set all professions',
  GET_ALL_DEPARTMENTS = '[Exhibition] Get all departments',
  SET_ALL_DEPARTMENTS = '[Exhibition] Set all departments',
  GET_ALL_OCCUPATIONAL_GROUPS = '[Exhibition] Get all occupational groups',
  SET_ALL_OCCUPATIONAL_GROUPS = '[Exhibition] Set all occupational groups',
  PARTIAL_RESET_REDUCER = '[Exhibition] Partially reset reducer',
  RESET_REDUCER = '[Exhibition] Reset reducer'
}

/**
 * Every action is comprised of at least a type and an optional
 * payload. Expressing actions as classes enables powerful
 * type checking in reducer functions.
 *
 * See Discriminated Unions: https://www.typescriptlang.org/docs/handbook/advanced-types.html#discriminated-unions
 */

export class SetSelectedExhibitionId implements Action {
  readonly type = ActionTypes.SET_SELECTED_EXHIBITION_ID;
  constructor(public payload: number) {}
}

export class SetIsFirstLoadDone implements Action {
  readonly type = ActionTypes.SET_IS_FIRST_LOAD_DONE;
}

export class GetListOfAllExhibitions implements Action {
  readonly type = ActionTypes.GET_LIST_OF_ALL_EXHIBITIONS;
  constructor(public payload: string) {}
}

export class AddAllExhibitionsToListAction implements Action {
  readonly type = ActionTypes.ADD_ALL_EXHIBITIONS_TO_LIST;
  constructor(public payload: ExhibitionModel[]) {}
}

export class GetExhibitionHistoryList implements Action {
  readonly type = ActionTypes.GET_EXHIBITION_HISTORY_LIST;
  constructor(public payload: number) {}
}

export class SetExhibitionHistoryList implements Action {
  readonly type = ActionTypes.SET_EXHIBITION_HISTORY_LIST;
  constructor(public payload: ExhibitionHistoryListModel[]) {}
}

export class GetScripts implements Action {
  readonly type = ActionTypes.GET_SCRIPTS;
  constructor(public payload: number) {}
}

export class SetScripts implements Action {
  readonly type = ActionTypes.SET_SCRIPTS;
  constructor(public payload: Scripts | null) {}
}

export class ReloadScripts implements Action {
  readonly type = ActionTypes.RELOAD_SCRIPTS;
  constructor(public payload: string) {}
}

export class ResetScripts implements Action {
  readonly type = ActionTypes.RESET_SCRIPTS;
}

export class GetTitles implements Action {
  readonly type = ActionTypes.GET_TITLES;
  constructor(public payload: number) {}
}

export class SetTitles implements Action {
  readonly type = ActionTypes.SET_TITLES;
  constructor(public payload: SelectOption[]) {}
}

export class GetProfessions implements Action {
  readonly type = ActionTypes.GET_PROFESSIONS;
  constructor(public payload: number) {}
}
export class SetProfessions implements Action {
  readonly type = ActionTypes.SET_PROFESSIONS;
  constructor(public payload: SelectOption[]) {}
}

export class GetDepartments implements Action {
  readonly type = ActionTypes.GET_DEPARTMENTS;
  constructor(public payload: number) {}
}

export class SetDepartments implements Action {
  readonly type = ActionTypes.SET_DEPARTMENTS;
  constructor(public payload: SelectOption[]) {}
}

export class GetOccupationalGroups implements Action {
  readonly type = ActionTypes.GET_OCCUPATIONAL_GROUPS;
  constructor(public payload: number) {}
}

export class SetOccupationalGroups implements Action {
  readonly type = ActionTypes.SET_OCCUPATIONAL_GROUPS;
  constructor(public payload: SelectOption[]) {}
}

export class GetAllTitles implements Action {
  readonly type = ActionTypes.GET_ALL_TITLES;
}

export class SetAllTitles implements Action {
  readonly type = ActionTypes.SET_ALL_TITLES;
  constructor(public payload: QuestionnaireDataInput[]) {}
}

export class GetAllProfessions implements Action {
  readonly type = ActionTypes.GET_ALL_PROFESSIONS;
}

export class SetAllProfessions implements Action {
  readonly type = ActionTypes.SET_ALL_PROFESSIONS;
  constructor(public payload: QuestionnaireDataInput[]) {}
}

export class GetAllDepartments implements Action {
  readonly type = ActionTypes.GET_ALL_DEPARTMENTS;
}

export class SetAllDepartments implements Action {
  readonly type = ActionTypes.SET_ALL_DEPARTMENTS;
  constructor(public payload: QuestionnaireDataInput[]) {}
}

export class GetAllOccupationalGroups implements Action {
  readonly type = ActionTypes.GET_ALL_OCCUPATIONAL_GROUPS;
}

export class SetAllOccupationalGroups implements Action {
  readonly type = ActionTypes.SET_ALL_OCCUPATIONAL_GROUPS;
  constructor(public payload: QuestionnaireDataInput[]) {}
}

export class PartialResetReducer implements Action {
  readonly type = ActionTypes.PARTIAL_RESET_REDUCER;
}

export class ResetReducer implements Action {
  readonly type = ActionTypes.RESET_REDUCER;
}

/**
 * Export a type alias of all actions in this action group
 * so that reducers can easily compose action types
 */
export type Actions =
  | SetSelectedExhibitionId
  | SetIsFirstLoadDone
  | GetListOfAllExhibitions
  | AddAllExhibitionsToListAction
  | GetExhibitionHistoryList
  | SetExhibitionHistoryList
  | GetScripts
  | SetScripts
  | ReloadScripts
  | ResetScripts
  | GetTitles
  | SetTitles
  | GetProfessions
  | SetProfessions
  | GetDepartments
  | SetDepartments
  | GetOccupationalGroups
  | SetOccupationalGroups
  | GetAllTitles
  | SetAllTitles
  | GetAllProfessions
  | SetAllProfessions
  | GetAllDepartments
  | SetAllDepartments
  | GetAllOccupationalGroups
  | SetAllOccupationalGroups
  | PartialResetReducer
  | ResetReducer;
